import {Injectable, OnDestroy} from '@angular/core';
import {GeneralService} from './general.service';
import {AuthService} from './auth.service';
import {
    concat,
    concatMap, distinctUntilChanged,
    Observable, of, repeat, share, Subject, switchMap, takeUntil,
} from 'rxjs';
import {map} from 'rxjs/operators';
import {NICEAgent} from '../models/NICE_Agent.model';
import {NICEAgentState} from '../models/NICE_AgentState';
import {DateTime} from 'luxon';
import {NICEContactState} from "../models/NICE_ContactState";

@Injectable({
    providedIn: 'root'
})
export class IcDataRealTimeService implements OnDestroy {
    private agentStatesLastPollDate: DateTime = DateTime.fromISO('1900-01-01');
    private stopAgentStates: Subject<void> = new Subject();
    private agentStateSubCount = 0;
    private agentStatePoller$: Observable<NICEAgentState[]>;
    private agentStateMap: Map<number, NICEAgentState> = new Map();

    private activeContactsLastUpdatedDate: DateTime = DateTime.fromISO('1900-01-01');
    private stopContactStates: Subject<void> = new Subject();
    private contactStateSubCount = 0;
    private contactStatePoller$: Observable<NICEContactState[]>;
    private contactStateMap: Map<number, any> = new Map();

    constructor(public genHttp: GeneralService,
                public auth: AuthService,
    )
    {
        this.initializeData();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this.stopPollingAgentStates();
        this.stopPollingContactStates()
    }

    initializeData() {
        this.agentStatesLastPollDate = DateTime.fromISO('1900-01-01');
        this.activeContactsLastUpdatedDate = null
       // this.activeContactsLastUpdatedDate = DateTime.local().minus({ days: 30 }).startOf('day');
        this.agentStateMap = new Map<number, NICEAgentState>();
        this.contactStateMap = new Map<number, NICEContactState>();
    }

    pollAgentStates(): Observable<NICEAgentState[]> {
        if (!this.agentStatePoller$) {
            this.agentStatePoller$ = of(null).pipe(
                concatMap(() => this.getAgentStates()),
                takeUntil(this.stopAgentStates),
                distinctUntilChanged(),
                repeat({delay: 5000}),
                share(),
            );
        }
        this.agentStateSubCount++;
        return this.agentStatePoller$;
    }

    stopPollingAgentStates(): void {
        this.agentStateSubCount--;
        if (this.agentStateSubCount === 0) {
            this.stopAgentStates.next();
            this.agentStatePoller$ = null;
        }
    }


    pollContactStates(): Observable<NICEContactState[]> {
        if (!this.contactStatePoller$) {
            this.contactStatePoller$ = of(null).pipe(
                concatMap(() => this.getContactStates()),
                takeUntil(this.stopContactStates),
                distinctUntilChanged(),
                repeat({delay: 5000}),
                share(),
            );
        }
        this.contactStateSubCount++;
        return this.contactStatePoller$;
    }

    stopPollingContactStates(): void {
        this.contactStateSubCount--;
        if (this.contactStateSubCount === 0) {
            this.stopContactStates.next();
            this.contactStatePoller$ = null;
        }
    }

    public getAgentStates(): Observable<NICEAgentState[]>
    {
        const URL = 'baseURL/services/v30.0/cache/agentstate?updatedSince=' + encodeURIComponent(this.agentStatesLastPollDate.toISO());
        return this.genHttp.icGet(URL)
            .pipe(map((response: any) => {
                this.agentStatesLastPollDate = DateTime.fromISO(response.resultSet.lastPollTime, {zone: 'utc'});
                let newAgentStates: NICEAgent[] = [];
                if (response && response.resultSet.agentState) {
                    newAgentStates = response.resultSet.agentState.map(data => new NICEAgentState(data));
                }
                newAgentStates.forEach(item => {
                    if(item.isActive) {
                        this.agentStateMap.set(item.agentId, {...this.agentStateMap.get(item.agentId), ...item})
                    }
                });
                return Array.from(this.agentStateMap.values());
            }),);
    }

    public getContactStates(nextUrl?: string,skip?: number): Observable<NICEContactState[]> {
        if(skip == null) {skip = 0;}
        let URL = 'baseURL/services/v30.0/contacts/states?top=10000&skip=' + skip;

        if (this.activeContactsLastUpdatedDate)
        {
            URL= URL + '&updatedSince=' + encodeURIComponent(this.activeContactsLastUpdatedDate.minus({ seconds: 60 }).toISO());
        }

        return this.genHttp.icGet(URL).pipe(
            switchMap((response: any) => {
                let newContactStates: NICEContactState[] = [];

                if (response && response.contactStates.length > 1) {
                    newContactStates = response.contactStates.map((data: any) => new NICEContactState(data));
                }

                newContactStates.forEach(item => {
                    if (item.contactStateName === 'EndContact') {
                        this.contactStateMap.delete(item.contactId);
                    } else {
                        this.contactStateMap.set(item.contactId, { ...this.contactStateMap.get(item.contactId), ...item });
                    }
                });

                if(response._links && response._links.next) {
                    // If there is a next url, make an another HTTP request
                    return concat(of(Array.from(this.contactStateMap.values())), this.getContactStates(response._links.next,skip + 10000));
                }
                else
                {
                    this.activeContactsLastUpdatedDate =   DateTime.fromISO(response.lastPollTime, {zone: 'utc'});
                }

                return of(Array.from(this.contactStateMap.values()));
            })
        );
    }
}
